const AJAX_HEADERS = { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' };

// Backed enum value, not a slug - see DeliveryMethodEnum.
const PICKUP_VALUE = 'Enlèvement au domaine';

function formatAmount(amount) {
    return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
}

function formatPercent(percent) {
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(percent);
}

/**
 * Parsed as Y/M/D components rather than `new Date(isoDate)` so a UTC
 * midnight parse can't roll the day back in a negative-offset browser -
 * same reasoning as the checkout tunnel (see services/cart.js).
 */
function formatDate(isoDate) {
    if (!isoDate) return null;

    const [year, month, day] = String(isoDate).split('-').map(Number);
    if (!year || !month || !day) return null;

    return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long' }).format(new Date(year, month - 1, day));
}

function field(form, name) {
    return form.querySelector(`[name$="[${name}]"]`);
}

/**
 * Writes the picked offer into the three transport amount fields. They stay
 * editable afterwards: what the admin leaves in them is what gets billed
 * (see OrderService::update()), this only saves them the typing.
 *
 * Insurance is the exception - it is a choice the customer made at checkout,
 * not something the offer decides, so an uninsured order stays uninsured and
 * an insured one is simply re-priced.
 */
function applyRateToCostFields(form, rate) {
    const shippingField = field(form, 'shippingCost');
    const insuranceField = field(form, 'insuranceCost');
    const otherField = field(form, 'otherShippingCosts');

    if (shippingField) shippingField.value = rate.shippingCostAllIn.toFixed(2);
    if (otherField) otherField.value = (rate.otherCostsAllIn ?? 0).toFixed(2);

    if (insuranceField) {
        const wasInsured = parseFloat(insuranceField.value) > 0;
        if (wasInsured) {
            insuranceField.value = (rate.insuranceCostAllIn ?? 0).toFixed(2);
        }
    }
}

function buildOption(form, block, rate) {
    const label = document.createElement('label');
    label.className = 'rate-options__option';

    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.name = 'adminRateOption';
    radio.value = rate.checksum;

    const textWrap = document.createElement('span');
    textWrap.className = 'rate-options__text';

    const details = document.createElement('span');
    const total = rate.shippingCostAllIn + (rate.otherCostsAllIn ?? 0);
    details.textContent = `${rate.serviceName || rate.carrier} : ${formatAmount(total)} €`;
    textWrap.appendChild(details);

    const deliveryDate = formatDate(rate.deliveryDate);
    if (deliveryDate) {
        const deliveryEl = document.createElement('span');
        deliveryEl.className = 'rate-options__delivery';
        deliveryEl.textContent = `Livraison estimée le ${deliveryDate}`;
        textWrap.appendChild(deliveryEl);
    }

    if (rate.packagingSummary) {
        const packagingEl = document.createElement('span');
        packagingEl.className = 'rate-options__packaging';
        packagingEl.textContent = rate.packagingSummary;
        textWrap.appendChild(packagingEl);
    }

    // The price above is what the customer pays, markup included (see
    // RateService::applyMarkup()) - spelled out here because an admin
    // re-quoting an order needs to see the carrier's own price behind it,
    // which the customer never does.
    if (rate.shippingMarkupPercent > 0) {
        const markupEl = document.createElement('span');
        markupEl.className = 'rate-options__markup';
        const margin = rate.shippingCostAllIn - rate.baseShippingCostAllIn;
        markupEl.textContent = `Transporteur ${formatAmount(rate.baseShippingCostAllIn)} € + ${formatPercent(rate.shippingMarkupPercent)} % de marge (${formatAmount(margin)} €)`;
        textWrap.appendChild(markupEl);
    }

    label.appendChild(radio);
    label.appendChild(textWrap);

    radio.addEventListener('change', () => {
        const checksumField = block.querySelector('[data-order-rates-checksum]');
        if (checksumField) checksumField.value = rate.checksum;
        applyRateToCostFields(form, rate);
        markUpToDate(block);
    });

    return label;
}

function markOutdated(block) {
    const outdated = block.querySelector('[data-order-rates-outdated]');
    const list = block.querySelector('[data-order-rates-list]');
    const messageEl = block.querySelector('[data-order-rates-message]');
    const checksumField = block.querySelector('[data-order-rates-checksum]');

    if (outdated) outdated.hidden = false;
    if (list) list.innerHTML = '';
    if (messageEl) messageEl.hidden = true;

    // Back to the checksum the order was loaded with: an offer quoted for
    // the address as it was a moment ago must not be frozen onto the one
    // being typed now. Restoring it rather than blanking it also keeps the
    // save path unchanged when the admin undoes their edit.
    if (checksumField) checksumField.value = checksumField.defaultValue;
}

function markUpToDate(block) {
    const outdated = block.querySelector('[data-order-rates-outdated]');
    if (outdated) outdated.hidden = true;
}

function renderRates(form, block, payload) {
    const list = block.querySelector('[data-order-rates-list]');
    const messageEl = block.querySelector('[data-order-rates-message]');
    const rates = payload.rates ?? [];

    if (list) list.innerHTML = '';

    if (messageEl) {
        messageEl.textContent = payload.message ?? '';
        messageEl.hidden = !payload.message;
    }

    if (!list || rates.length === 0) {
        return;
    }

    rates.forEach((rate) => list.appendChild(buildOption(form, block, rate)));
}

/**
 * Asks eShipping for offers matching the form as it currently stands - not
 * the saved order - by posting the whole edit form to
 * OrderController::rates(), so the address, the lines and the shipping date
 * being quoted are exactly the ones about to be saved.
 */
async function fetchRates(form, block, button) {
    const url = block.dataset.orderRatesUrl;
    if (!url) return;

    const idleLabel = button.textContent;
    button.disabled = true;
    button.textContent = button.dataset.orderRatesLoadingLabel || idleLabel;

    try {
        const response = await fetch(url, { method: 'POST', body: new FormData(form), headers: AJAX_HEADERS });
        renderRates(form, block, await response.json());
    } catch {
        renderRates(form, block, { rates: [], message: 'Le calcul a échoué. Réessayez.' });
    } finally {
        button.disabled = false;
        button.textContent = idleLabel;
    }
}

/**
 * An enlèvement au domaine is never quoted (see
 * OrderService::requiresRequote()), so the whole block steps out of the way
 * instead of offering a button that can only answer "pas de cotation".
 * The pickup note follows the other way round: it only applies while the
 * goods are collected at the estate.
 *
 * The shipping-address fields go with the quote: an order collected at the
 * estate has no address of its own (see OrderDto::validateDeliveryFields(),
 * which stops requiring them), so they are hidden AND disabled - exactly what
 * the checkout tunnel does (see services/cart.js). Disabling is what makes
 * the difference: OrderService::update() writes every address field it is
 * given, so a field left enabled would carry the old address onto an order
 * nobody ships.
 */
function applyDeliveryMethod(form, block) {
    const isPickup = form.querySelector('[name$="[deliveryMethod]"]:checked')?.value === PICKUP_VALUE;

    block.hidden = isPickup;
    form.querySelectorAll('[data-pickup-only]').forEach((note) => {
        note.hidden = !isPickup;
    });

    form.querySelectorAll('[data-shipping-only]').forEach((wrapper) => {
        wrapper.hidden = isPickup;
        wrapper.querySelectorAll('input, select').forEach((field) => {
            field.disabled = isPickup;
        });
    });
}

/**
 * Re-quote block on the admin order edit screen: fetches eShipping offers
 * for the form as it stands, freezes the picked one through the hidden
 * rateChecksum field, and says out loud when an edit has left the offers on
 * screen behind. The server decides in the end - see
 * OrderController::edit(), which refuses a quote-invalidating change that
 * carries neither a fresh offer nor "conserver le tarif actuel".
 */
function initOrderRates() {
    document.querySelectorAll('[data-order-rates]').forEach((block) => {
        const form = block.closest('form');
        const button = block.querySelector('[data-order-rates-fetch]');

        if (!form || !button) {
            return;
        }

        applyDeliveryMethod(form, block);

        button.addEventListener('click', () => fetchRates(form, block, button));

        // Anything inside a watched region moves the quote: the address, the
        // shipping date, a quantity, a product, a line added or removed.
        form.querySelectorAll('[data-order-requote-watch]').forEach((region) => {
            ['input', 'change'].forEach((eventName) => {
                region.addEventListener(eventName, () => {
                    applyDeliveryMethod(form, block);
                    markOutdated(block);
                });
            });
            region.addEventListener('click', (event) => {
                if (event.target.closest('[data-order-line-remove], [data-order-lines-add]')) {
                    markOutdated(block);
                }
            });
        });
    });
}

export default initOrderRates;
